import { useState, useEffect, useRef } from 'react';
import { useLocation, Link, useLocation as useRouteLocation } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Search, Clock, MapPin, User, Calendar, X, History,
  TrendingUp, Bell, Check, FileText, MessageSquare, Bus, Bot,
  Home, ClipboardList, GraduationCap, Users, BookOpen,
  RotateCcw, LayoutGrid, List, ChevronLeft, ChevronRight, PackageSearch,
} from 'lucide-react';
import { usePrograms, useStoreActions, useSettingsStore, useUserPreferences } from '@/store/useStore';
import { UnifiedProgram } from '@/types';
import { DataProcessor } from '@/utils/dataProcessor';
import { compareTimeStrings } from '@/utils/timeUtils';
import { getOrderedDays } from '@/utils/dayUtils';
import Logo from '@/components/Logo';
import { useDashboard } from '@/contexts/DashboardContext';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

// ─── Design tokens (mirrors home dashboard) ───────────────────────────────────
const T = {
  bg:         '#0A0A0A',
  surface:    'rgba(255,255,255,.03)',
  surfaceHov: 'rgba(255,255,255,.055)',
  border:     'rgba(255,255,255,.07)',
  borderHov:  'rgba(255,255,255,.13)',
  teal:       '#14B8A6',
  tealBg:     'rgba(20,184,166,.1)',
  tealBorder: 'rgba(20,184,166,.22)',
  blue:       '#60A5FA',
  text:       '#fff',
  text50:     'rgba(255,255,255,.5)',
  text35:     'rgba(255,255,255,.35)',
  text25:     'rgba(255,255,255,.25)',
  text15:     'rgba(255,255,255,.15)',
  mono:       "'JetBrains Mono', monospace",
  serif:      "'Instrument Serif', serif",
  sans:       "'Outfit', sans-serif",
};

// ─── Sidebar nav config ───────────────────────────────────────────────────────
const MAIN_NAV = [
  { path: '/dashboard/home',     Icon: Home,          label: 'Home'       },
  { path: '/dashboard/personal', Icon: Calendar,      label: 'Timetable'  },
  { path: '/dashboard/browse',   Icon: LayoutGrid,    label: 'Browse'     },
  { path: '/dashboard/rooms',    Icon: MapPin,        label: 'Campus Map' },
  { path: '/dashboard/bus',      Icon: Bus,           label: 'Bus Routes' },
  { path: '/dashboard/lost-found', Icon: PackageSearch, label: 'Lost & Found' },
];
const ACADEMIC_NAV = [
  { path: '/dashboard/calendar',   Icon: Calendar,      label: 'Academics'  },
  { path: '/dashboard/exams',      Icon: FileText,      label: 'Exams'      },
  { path: '/dashboard/owlet',      Icon: Bot,           label: 'Owlet AI'   },
  { path: '/dashboard/feedback',   Icon: MessageSquare, label: 'Feedback'   },
  { path: '/dashboard/attendance', Icon: ClipboardList, label: 'Attendance' },
];
const TOP_NAV = [
  { path: '/dashboard/home',     label: 'Home'      },
  { path: '/dashboard/personal', label: 'Timetable' },
  { path: '/dashboard/browse',   label: 'Browse'    },
  { path: '/dashboard/rooms',    label: 'Rooms'     },
  { path: '/dashboard/exams',    label: 'Exams'     },
];

// ─── Primitives ───────────────────────────────────────────────────────────────
function MonoLabel({ children, color = T.text25, size = 8 }: { children: React.ReactNode; color?: string; size?: number }) {
  return (
    <span style={{
      fontFamily: T.mono, fontSize: size, letterSpacing: '.16em',
      color, textTransform: 'uppercase' as const,
    }}>{children}</span>
  );
}

function SidebarLink({ path, Icon, label }: { path: string; Icon: any; label: string }) {
  const { pathname } = useRouteLocation();
  const active = pathname === path || pathname.startsWith(path + '/');
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={path}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '9px 10px', borderRadius: 9, textDecoration: 'none',
        fontSize: 12, transition: '.15s',
        background: active ? T.tealBg : hov ? 'rgba(255,255,255,.04)' : 'transparent',
        border: `1px solid ${active ? T.tealBorder : 'transparent'}`,
        color: active ? T.teal : hov ? 'rgba(255,255,255,.75)' : 'rgba(255,255,255,.4)',
      }}
    >
      <Icon size={15} aria-hidden />
      <span>{label}</span>
    </Link>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{
      fontFamily: T.mono, fontSize: 8, letterSpacing: '.18em',
      color: 'rgba(255,255,255,.2)', padding: '10px 10px 6px',
    }}>{text}</div>
  );
}

function TopNavLink({ path, label }: { path: string; label: string }) {
  const { pathname } = useRouteLocation();
  const active = pathname === path || pathname.startsWith(path + '/');
  const [hov, setHov] = useState(false);
  return (
    <Link
      to={path}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        fontSize: 12, textDecoration: 'none', transition: '.2s',
        color: active ? '#fff' : hov ? 'rgba(255,255,255,.65)' : 'rgba(255,255,255,.35)',
        paddingBottom: 2,
        borderBottom: active ? `1px solid rgba(20,184,166,.5)` : '1px solid transparent',
      }}
    >{label}</Link>
  );
}

function NavIconBtn({ children, onClick, badge }: { children: React.ReactNode; onClick?: () => void; badge?: number }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
        background: hov ? 'rgba(255,255,255,.08)' : 'rgba(255,255,255,.05)',
        border: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: hov ? '#fff' : T.text50,
        cursor: 'pointer', transition: '.2s', position: 'relative',
      }}
    >
      {children}
      {badge && badge > 0 ? (
        <span style={{
          position: 'absolute', top: -3, right: -3,
          width: 14, height: 14, borderRadius: '50%',
          background: '#EF4444', border: `2px solid ${T.bg}`,
          fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, color: '#fff',
        }}>{badge}</span>
      ) : null}
    </button>
  );
}

// ─── Filter rail components ───────────────────────────────────────────────────
function FilterGroup({ icon: Icon, label, children, active }: { icon: any; label: string; children: React.ReactNode; active?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 2px' }}>
        <Icon size={11} color={active ? T.teal : T.text35} />
        <MonoLabel color={active ? T.teal : T.text35}>{label}</MonoLabel>
      </div>
      {children}
    </div>
  );
}

function ComboboxFilter({
  value, options, onChange, placeholder, searchPlaceholder,
}: {
  value: string; options: string[]; onChange: (v: string) => void;
  placeholder: string; searchPlaceholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [hov, setHov] = useState(false);
  const active = value !== 'all';
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onMouseEnter={() => setHov(true)}
          onMouseLeave={() => setHov(false)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            width: '100%', padding: '8px 12px', borderRadius: 8,
            background: active ? T.tealBg : hov ? T.surfaceHov : T.surface,
            border: `1px solid ${active ? T.tealBorder : hov ? T.borderHov : T.border}`,
            color: active ? T.teal : T.text50,
            fontFamily: T.sans, fontSize: 12, cursor: 'pointer', transition: '.15s',
            textAlign: 'left',
          }}
        >
          <span style={{
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          }}>
            {active ? value : placeholder}
          </span>
          {active && (
            <X
              size={11}
              onClick={(e) => { e.stopPropagation(); onChange('all'); }}
              style={{ flexShrink: 0, opacity: .7, marginLeft: 6 }}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent style={{ background: '#141414', border: `1px solid ${T.borderHov}`, borderRadius: 10, padding: 0, width: 280 }}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="all" onSelect={() => { onChange('all'); setOpen(false); }}>
                <Check size={14} style={{ marginRight: 8, opacity: value === 'all' ? 1 : 0 }} />All
              </CommandItem>
              {options.map(opt => (
                <CommandItem key={opt} value={opt} onSelect={v => { onChange(v); setOpen(false); }}>
                  <Check size={14} style={{ marginRight: 8, opacity: value === opt ? 1 : 0 }} />{opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function SegmentedFilter({ value, options, onChange }: {
  value: string; options: { v: string; label: string }[]; onChange: (v: string) => void;
}) {
  return (
    <div style={{
      display: 'flex', background: T.surface,
      border: `1px solid ${T.border}`, borderRadius: 8, padding: 3, gap: 3,
    }}>
      {options.map(({ v, label }) => {
        const active = value === v;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            style={{
              flex: 1, padding: '6px 0', borderRadius: 6,
              background: active ? T.tealBg : 'none',
              border: `1px solid ${active ? T.tealBorder : 'transparent'}`,
              color: active ? T.teal : T.text50,
              fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em',
              cursor: 'pointer', transition: '.15s', textAlign: 'center' as const,
            }}
          >{label}</button>
        );
      })}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function BrowseTimetablesDesktop() {
  const dashboard       = useDashboard();
  const location        = useLocation();
  const userPreferences = useUserPreferences();
  const programs        = usePrograms();
  const { initializeData } = useStoreActions();

  const [searchQuery,          setSearchQuery]          = useState('');
  const [showSearchSuggestions,setShowSearchSuggestions]= useState(false);
  const [searchSuggestions,    setSearchSuggestions]    = useState<any[]>([]);
  const [recentSearches,       setRecentSearches]       = useState<string[]>([]);
  const [selectedCategory,     setSelectedCategory]     = useState('all');
  const [selectedProgram,      setSelectedProgram]      = useState('all');
  const [selectedTeacher,      setSelectedTeacher]      = useState('all');
  const [selectedCourse,       setSelectedCourse]       = useState('all');
  const [selectedSemester,     setSelectedSemester]     = useState('all');
  const [selectedGroup,        setSelectedGroup]        = useState('all');
  const [selectedTimetable,    setSelectedTimetable]    = useState<UnifiedProgram | null>(null);
  const [showDetails,          setShowDetails]          = useState(false);
  const [selectedDayView,      setSelectedDayView]      = useState(new Date().toLocaleDateString('en-US', { weekday: 'long' }));
  const [viewMode,             setViewMode]             = useState<'day' | 'week'>('day');
  const [searchFocused,        setSearchFocused]        = useState(false);
  const [showFilters,          setShowFilters]          = useState(true);

  const searchInputRef     = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    initializeData();
    const saved = localStorage.getItem('recentTimetableSearches');
    if (saved) setRecentSearches(JSON.parse(saved));
  }, [initializeData]);

  // Apply search query passed via navigation state
  useEffect(() => {
    if (location.state?.searchQuery && programs.length > 0) {
      const query = location.state.searchQuery;
      const type  = location.state.type;
      const allCourses  = Array.from(new Set(programs.flatMap(p => Object.values(p.schedule).flatMap((cs: any) => cs.map((c: any) => c.course_name || c.course))).filter(Boolean)));
      const allTeachers = Array.from(new Set(programs.flatMap(p => Object.values(p.schedule).flatMap((cs: any) => cs.map((c: any) => c.instructor || c.teacher))).filter(Boolean)));
      if (type === 'course') {
        const m = allCourses.find(c => c.toLowerCase() === query.toLowerCase());
        m ? setSelectedCourse(m) : setSearchQuery(query);
      } else if (type === 'teacher') {
        const m = allTeachers.find(t => t.toLowerCase() === query.toLowerCase());
        m ? setSelectedTeacher(m) : setSearchQuery(query);
      } else {
        const sl = query.toLowerCase().trim();
        const mt = allTeachers.find(t => t.toLowerCase() === sl);
        const mc = allCourses.find(c => c.toLowerCase() === sl);
        if (mt) setSelectedTeacher(mt);
        else if (mc) setSelectedCourse(mc);
        else setSearchQuery(query);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state, programs]);

  // Search suggestions
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const dp = DataProcessor.getInstance();
      const ql = searchQuery.toLowerCase();
      const out: any[] = [];
      programs.filter(p => p.program.toLowerCase().includes(ql)).slice(0, 2).forEach(p => {
        out.push({ type: 'program', text: p.program, description: `${p.department} · Sem ${p.semester}` });
      });
      dp.getTeachers().filter(t => t.toLowerCase().includes(ql)).slice(0, 2).forEach(t => {
        out.push({ type: 'teacher', text: t, description: 'Instructor' });
      });
      dp.getCourses().filter(c => c.toLowerCase().includes(ql)).slice(0, 2).forEach(c => {
        out.push({ type: 'course', text: c, description: 'Course' });
      });
      setSearchSuggestions(out);
    } else setSearchSuggestions([]);
  }, [searchQuery, programs]);

  // Click outside to dismiss suggestions
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setShowSearchSuggestions(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleSearchSelect = (text: string) => {
    setShowSearchSuggestions(false);
    if (text.trim()) {
      const r = [text, ...recentSearches.filter(s => s !== text)].slice(0, 5);
      setRecentSearches(r);
      localStorage.setItem('recentTimetableSearches', JSON.stringify(r));
    }
    const dp = DataProcessor.getInstance();
    const sl = text.toLowerCase().trim();
    const mt = dp.getTeachers().find(t => t.toLowerCase() === sl);
    if (mt) { setSelectedTeacher(mt); setSearchQuery(''); return; }
    const mc = uniqueCourses.find(c => c.toLowerCase() === sl);
    if (mc) { setSelectedCourse(mc); setSearchQuery(''); return; }
    const mp = uniquePrograms.find(p => p.toLowerCase() === sl);
    if (mp) { setSelectedProgram(mp); setSearchQuery(''); return; }
    setSearchQuery(text);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentTimetableSearches');
  };

  // Derived data
  const uniquePrograms  = Array.from(new Set(programs.map(p => p.program))).sort();
  const uniqueSemesters = Array.from(new Set(programs.map(p => p.semester))).sort();
  const uniqueGroups    = Array.from(new Set(programs.map(p => p.group))).sort();
  const uniqueTeachers  = Array.from(new Set(programs.flatMap(p => Object.values(p.schedule).flatMap((cs: any) => cs.map((c: any) => c.instructor || c.teacher))).filter(Boolean))).sort();
  const uniqueCourses   = Array.from(new Set(programs.flatMap(p => Object.values(p.schedule).flatMap((cs: any) => cs.map((c: any) => c.course_name || c.course))).filter(Boolean))).sort();

  const filteredPrograms = programs.filter(p => {
    const sl = searchQuery.toLowerCase().trim();
    const matchesSearch = searchQuery === '' || (() => {
      if (Object.values(p.schedule).some((cs: any) => cs.some((c: any) => (c.course_name || c.course || '').toLowerCase() === sl))) return true;
      if (Object.values(p.schedule).some((cs: any) => cs.some((c: any) => (c.instructor || c.teacher || '').toLowerCase() === sl))) return true;
      if (p.program.toLowerCase().includes(sl)) return true;
      if (p.department.toLowerCase().includes(sl)) return true;
      if (p.group.toLowerCase().includes(sl)) return true;
      if (Object.values(p.schedule).some((cs: any) => cs.some((c: any) => {
        const cn = (c.course_name || c.course || '').toLowerCase();
        const ins = (c.instructor || c.teacher || '').toLowerCase();
        const loc = (c.location || '').toLowerCase();
        return cn.includes(sl) || ins.includes(sl) || loc.includes(sl);
      }))) return true;
      return false;
    })();
    const matchesCategory = selectedCategory === 'all' || (() => {
      const pl = p.program.toLowerCase();
      if (selectedCategory === 'bachelor') return pl.includes('bachelor') || pl.includes('bs ') || pl.includes('bba') || pl.includes('bcs');
      if (selectedCategory === 'master')   return pl.includes('master')   || pl.includes('ms ') || pl.includes('mba');
      return false;
    })();
    const matchesProgram  = selectedProgram  === 'all' || p.program  === selectedProgram;
    const matchesSemester = selectedSemester === 'all' || p.semester === selectedSemester;
    const matchesGroup    = selectedGroup    === 'all' || p.group    === selectedGroup;
    const matchesTeacher  = selectedTeacher  === 'all' || Object.values(p.schedule).some((cs: any) => cs.some((c: any) => (c.instructor || c.teacher) === selectedTeacher));
    const matchesCourse   = selectedCourse   === 'all' || Object.values(p.schedule).some((cs: any) => cs.some((c: any) => (c.course_name || c.course) === selectedCourse));
    return matchesSearch && matchesCategory && matchesProgram && matchesSemester && matchesGroup && matchesTeacher && matchesCourse;
  });

  const activeFilterCount = [selectedCategory, selectedProgram, selectedTeacher, selectedCourse, selectedSemester, selectedGroup].filter(f => f !== 'all').length;
  const clearAll = () => {
    setSelectedCategory('all'); setSelectedProgram('all'); setSelectedTeacher('all');
    setSelectedCourse('all');   setSelectedSemester('all'); setSelectedGroup('all');
    setSearchQuery('');
  };

  const numberToRoman = (num: number | string) => {
    const n = typeof num === 'string' ? parseInt(num) : num;
    return (['','I','II','III','IV','V','VI','VII','VIII','IX','X'][n]) || String(num);
  };

  const unreadCount = dashboard ? dashboard.generateNotifications().filter((n: any) => !dashboard.readNotifications.has(n.id)).length : 0;

  return (
    <div
      className="hidden lg:flex"
      style={{ background: T.bg, fontFamily: T.sans, color: T.text, minHeight: '100vh', flexDirection: 'column' }}
    >
      {/* ── Top bar ────────────────────────────────────────────────────────── */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 28px', borderBottom: `1px solid ${T.border}`,
        background: T.bg, position: 'sticky', top: 0, zIndex: 50, flexShrink: 0,
      }}>
        <Logo size="sm" clickable navigateTo="/dashboard/home" />
        <div style={{ display: 'flex', gap: 24 }}>
          {TOP_NAV.map(({ path, label }) => <TopNavLink key={path} path={path} label={label} />)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NavIconBtn onClick={() => dashboard?.setShowSearch(true)}>
            <Search size={14} />
          </NavIconBtn>
          <NavIconBtn badge={unreadCount} onClick={() => {
            if (dashboard) {
              dashboard.setShowNotifications(!dashboard.showNotifications);
              if (!dashboard.showNotifications) setTimeout(() => dashboard.markNotificationsAsRead(), 100);
            }
          }}>
            <Bell size={14} />
          </NavIconBtn>
          <button
            onClick={() => dashboard?.setShowUserMenu(!dashboard?.showUserMenu)}
            style={{
              width: 32, height: 32, borderRadius: 9,
              background: T.tealBg, border: `1px solid ${T.tealBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: T.teal, fontFamily: T.mono, fontSize: 10, fontWeight: 500, cursor: 'pointer',
            }}
          >{userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}</button>
        </div>
      </header>

      {/* ── Body: nav sidebar + filter rail + content ──────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Nav sidebar ─────────────────────────────────────────────────── */}
        <aside style={{
          width: 220, flexShrink: 0,
          borderRight: `1px solid ${T.border}`, padding: '20px 16px',
          display: 'flex', flexDirection: 'column',
          background: T.bg, overflowY: 'auto',
        }}>
          <SectionLabel text="MAIN" />
          {MAIN_NAV.map(item => <SidebarLink key={item.path} {...item} />)}
          <SectionLabel text="ACADEMICS" />
          {ACADEMIC_NAV.map(item => <SidebarLink key={item.path} {...item} />)}

          <div style={{ flex: 1 }} />

          {/* Profile chip */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 10px', borderTop: `1px solid ${T.border}`, marginTop: 8,
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: 9, flexShrink: 0,
              background: T.tealBg, border: `1px solid ${T.tealBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: T.teal, fontFamily: T.mono, fontSize: 10, fontWeight: 500,
            }}>{userPreferences?.name?.charAt(0).toUpperCase() ?? 'U'}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userPreferences?.name || 'Student'}
              </div>
              <div style={{
                fontFamily: T.mono, fontSize: 8, letterSpacing: '.06em',
                color: T.text25, marginTop: 1,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {[
                  userPreferences?.program,
                  userPreferences?.semester ? `SEM ${numberToRoman(userPreferences.semester)}` : null,
                  userPreferences?.group ? `GRP ${userPreferences.group}` : null,
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>
        </aside>

        {/* ── Filter rail (collapsible) ──────────────────────────────────── */}
        <aside style={{
          width: showFilters ? 260 : 0,
          flexShrink: 0,
          borderRight: showFilters ? `1px solid ${T.border}` : 'none',
          padding: showFilters ? '24px 20px' : '24px 0',
          display: 'flex', flexDirection: 'column', gap: 18,
          overflowY: 'auto', overflowX: 'hidden',
          background: T.bg,
          transition: 'width .3s ease, padding .3s ease',
          opacity: showFilters ? 1 : 0,
        }}>
          {/* Filter header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 220 }}>
            <span style={{ fontFamily: T.serif, fontSize: 18, fontWeight: 400, letterSpacing: '-.01em' }}>
              Filters
            </span>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAll}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: T.mono, fontSize: 8, color: T.text35, letterSpacing: '.1em',
                }}
              >
                <RotateCcw size={9} /> RESET
              </button>
            )}
          </div>

          {/* Active filter count badge */}
          {activeFilterCount > 0 && (
            <div style={{
              background: T.tealBg, border: `1px solid ${T.tealBorder}`,
              borderRadius: 8, padding: '8px 12px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              minWidth: 220,
            }}>
              <MonoLabel color={T.teal}>
                {activeFilterCount} ACTIVE · {filteredPrograms.length} RESULTS
              </MonoLabel>
            </div>
          )}

          {/* Category segmented */}
          <div style={{ minWidth: 220 }}>
            <FilterGroup icon={GraduationCap} label="Level" active={selectedCategory !== 'all'}>
              <SegmentedFilter
                value={selectedCategory}
                onChange={setSelectedCategory}
                options={[
                  { v: 'all',      label: 'ALL'   },
                  { v: 'bachelor', label: "BACH'S" },
                  { v: 'master',   label: "MAST'S" },
                ]}
              />
            </FilterGroup>
          </div>

          {/* Program */}
          <div style={{ minWidth: 220 }}>
            <FilterGroup icon={LayoutGrid} label="Program" active={selectedProgram !== 'all'}>
              <ComboboxFilter
                value={selectedProgram}
                options={uniquePrograms}
                onChange={setSelectedProgram}
                placeholder="All programs"
                searchPlaceholder="Search program…"
              />
            </FilterGroup>
          </div>

          {/* Teacher */}
          <div style={{ minWidth: 220 }}>
            <FilterGroup icon={Users} label="Instructor" active={selectedTeacher !== 'all'}>
              <ComboboxFilter
                value={selectedTeacher}
                options={uniqueTeachers}
                onChange={setSelectedTeacher}
                placeholder="All instructors"
                searchPlaceholder="Search instructor…"
              />
            </FilterGroup>
          </div>

          {/* Course */}
          <div style={{ minWidth: 220 }}>
            <FilterGroup icon={BookOpen} label="Course" active={selectedCourse !== 'all'}>
              <ComboboxFilter
                value={selectedCourse}
                options={uniqueCourses}
                onChange={setSelectedCourse}
                placeholder="All courses"
                searchPlaceholder="Search course…"
              />
            </FilterGroup>
          </div>

          {/* Semester + Group (side by side) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, minWidth: 220 }}>
            <FilterGroup icon={Calendar} label="Semester" active={selectedSemester !== 'all'}>
              <ComboboxFilter
                value={selectedSemester}
                options={uniqueSemesters}
                onChange={setSelectedSemester}
                placeholder="Any"
                searchPlaceholder="Find sem…"
              />
            </FilterGroup>
            <FilterGroup icon={Users} label="Group" active={selectedGroup !== 'all'}>
              <ComboboxFilter
                value={selectedGroup}
                options={uniqueGroups}
                onChange={setSelectedGroup}
                placeholder="Any"
                searchPlaceholder="Find group…"
              />
            </FilterGroup>
          </div>
        </aside>

        {/* ── Main content ────────────────────────────────────────────────── */}
        <main style={{ flex: 1, overflowY: 'auto', padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: 22 }}>

          {/* ── Page header ────────────────────────────────────────────── */}
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
              {/* Filter toggle button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                style={{
                  width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                  background: showFilters ? T.tealBg : T.surface,
                  border: `1px solid ${showFilters ? T.tealBorder : T.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: showFilters ? T.teal : T.text50,
                  cursor: 'pointer', transition: '.2s',
                }}
                title={showFilters ? 'Hide filters' : 'Show filters'}
                onMouseEnter={e => {
                  if (!showFilters) {
                    e.currentTarget.style.background = T.surfaceHov;
                    e.currentTarget.style.borderColor = T.borderHov;
                  }
                }}
                onMouseLeave={e => {
                  if (!showFilters) {
                    e.currentTarget.style.background = T.surface;
                    e.currentTarget.style.borderColor = T.border;
                  }
                }}
              >
                {showFilters ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
              </button>
              
              <h1 style={{ fontFamily: T.serif, fontSize: 30, fontWeight: 400, letterSpacing: '-.02em', margin: 0, lineHeight: 1 }}>
                Browse Timetables
              </h1>
              {filteredPrograms.length > 0 && (
                <span style={{
                  fontFamily: T.mono, fontSize: 9, letterSpacing: '.12em', color: T.teal,
                  background: T.tealBg, border: `1px solid ${T.tealBorder}`,
                  borderRadius: 4, padding: '3px 9px',
                }}>{filteredPrograms.length} PROGRAMS</span>
              )}
            </div>
            <MonoLabel>Explore schedules across all departments</MonoLabel>
          </div>

          {/* ── Search bar ─────────────────────────────────────────────── */}
          <div ref={searchContainerRef} style={{ position: 'relative' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: T.surface,
              border: `1px solid ${searchFocused ? T.tealBorder : T.border}`,
              borderRadius: 12, padding: '0 14px',
              transition: 'border-color .2s',
              boxShadow: searchFocused ? `0 0 0 3px rgba(20,184,166,.08)` : 'none',
            }}>
              <Search size={15} color={searchFocused ? T.teal : T.text35} style={{ flexShrink: 0, transition: 'color .2s' }} />
              <input
                ref={searchInputRef}
                placeholder="Search course, instructor, room, program…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => { setSearchFocused(true); setShowSearchSuggestions(true); }}
                onBlur={() => setSearchFocused(false)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { setShowSearchSuggestions(false); if (searchQuery.trim()) handleSearchSelect(searchQuery); }
                  else if (e.key === 'Escape') { setShowSearchSuggestions(false); searchInputRef.current?.blur(); }
                }}
                style={{
                  flex: 1, background: 'none', border: 'none', outline: 'none',
                  color: T.text, fontFamily: T.sans, fontSize: 13, height: 46,
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setShowSearchSuggestions(false); searchInputRef.current?.focus(); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.text35, display: 'flex', alignItems: 'center', padding: 0 }}
                >
                  <X size={14} />
                </button>
              )}
              <kbd style={{
                fontFamily: T.mono, fontSize: 9, color: T.text25, letterSpacing: '.06em',
                background: 'rgba(255,255,255,.04)', border: `1px solid ${T.border}`,
                borderRadius: 4, padding: '2px 6px',
              }}>↵</kbd>
            </div>

            {/* Suggestions dropdown */}
            {showSearchSuggestions && (searchQuery.length > 0 || recentSearches.length > 0) && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
                background: '#141414', border: `1px solid ${T.borderHov}`,
                borderRadius: 12, zIndex: 50, overflow: 'hidden',
                boxShadow: '0 16px 40px rgba(0,0,0,.5)',
              }}>
                {searchQuery.length === 0 && recentSearches.length > 0 && (
                  <div style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <MonoLabel>Recent</MonoLabel>
                      <button onClick={clearRecentSearches} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.mono, fontSize: 8, color: T.text35, letterSpacing: '.08em' }}>CLEAR</button>
                    </div>
                    {recentSearches.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => handleSearchSelect(s)}
                        style={{
                          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 6px', borderRadius: 8, color: T.text50,
                          fontFamily: T.sans, fontSize: 12, textAlign: 'left',
                        }}
                      >
                        <History size={12} color={T.text25} />{s}
                      </button>
                    ))}
                  </div>
                )}
                {searchQuery.length > 0 && searchSuggestions.length > 0 && (
                  <div style={{ padding: '14px 16px' }}>
                    <MonoLabel>Suggestions</MonoLabel>
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {searchSuggestions.map((s, i) => (
                        <button
                          key={i}
                          onClick={() => handleSearchSelect(s.text)}
                          style={{
                            width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '8px 8px', borderRadius: 8, textAlign: 'left',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = T.surfaceHov)}
                          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                        >
                          <div style={{
                            width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                            background: T.tealBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {s.type === 'teacher' ? <User size={12} color={T.teal} /> :
                             s.type === 'course'  ? <BookOpen size={12} color={T.teal} /> :
                                                    <TrendingUp size={12} color={T.teal} />}
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: T.text, fontFamily: T.sans }}>{s.text}</div>
                            <div style={{ fontFamily: T.mono, fontSize: 8, color: T.text25, letterSpacing: '.06em', marginTop: 2 }}>
                              {s.description.toUpperCase()}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {searchQuery.length > 0 && searchSuggestions.length === 0 && (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <MonoLabel color={T.text25}>No matches for "{searchQuery}"</MonoLabel>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── View mode + day selector ───────────────────────────────── */}
          <ScheduleHeader
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            selectedDay={selectedDayView}
            onDayChange={setSelectedDayView}
          />

          {/* ── Schedule content ───────────────────────────────────────── */}
          <DayWeekView
            programs={filteredPrograms}
            viewMode={viewMode}
            selectedDay={selectedDayView}
            activeFilters={{ teacher: selectedTeacher, course: selectedCourse }}
            onViewDetails={p => { setSelectedTimetable(p); setShowDetails(true); }}
          />
        </main>
      </div>

      {/* ── Details modal ──────────────────────────────────────────────── */}
      {showDetails && selectedTimetable && (
        <TimetableDetailsModal
          program={selectedTimetable}
          isOpen={showDetails}
          onClose={() => { setShowDetails(false); setSelectedTimetable(null); }}
          activeFilters={{ teacher: selectedTeacher, course: selectedCourse }}
        />
      )}
    </div>
  );
}

// ─── Schedule header (view mode toggle + day pills) ──────────────────────────
function ScheduleHeader({
  viewMode, onViewModeChange, selectedDay, onDayChange,
}: {
  viewMode: 'day' | 'week'; onViewModeChange: (m: 'day' | 'week') => void;
  selectedDay: string; onDayChange: (d: string) => void;
}) {
  const { preferences } = useSettingsStore();
  const days  = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      {/* View mode toggle */}
      <div style={{
        display: 'flex', background: T.surface,
        border: `1px solid ${T.border}`, borderRadius: 9, padding: 3, gap: 3,
      }}>
        {(['day', 'week'] as const).map(m => {
          const active = viewMode === m;
          const Icon = m === 'day' ? List : LayoutGrid;
          return (
            <button
              key={m}
              onClick={() => onViewModeChange(m)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 6,
                background: active ? T.tealBg : 'none',
                border: `1px solid ${active ? T.tealBorder : 'transparent'}`,
                color: active ? T.teal : T.text50,
                fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em',
                cursor: 'pointer', transition: '.15s',
              }}
            >
              <Icon size={11} />
              {m === 'day' ? 'DAY' : 'WEEK'}
            </button>
          );
        })}
      </div>

      {/* Day pills (only in day view) */}
      {viewMode === 'day' && (
        <div style={{ display: 'flex', gap: 6, flex: 1, overflowX: 'auto' }}>
          {days.map(day => {
            const isToday    = day === today;
            const isSelected = day === selectedDay;
            return (
              <button
                key={day}
                onClick={() => onDayChange(day)}
                style={{
                  padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                  background: isSelected ? T.tealBg : T.surface,
                  border: `1px solid ${isSelected ? T.tealBorder : T.border}`,
                  color: isSelected ? T.teal : isToday ? 'rgba(255,255,255,.65)' : T.text50,
                  fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em',
                  whiteSpace: 'nowrap', transition: '.15s', position: 'relative',
                }}
              >
                {day.substring(0, 3).toUpperCase()}
                {isToday && !isSelected && (
                  <span style={{
                    position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)',
                    width: 3, height: 3, borderRadius: '50%', background: T.teal,
                  }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Day / Week View ──────────────────────────────────────────────────────────
function DayWeekView({
  programs, viewMode, selectedDay, activeFilters, onViewDetails,
}: {
  programs: UnifiedProgram[]; viewMode: 'day' | 'week'; selectedDay: string;
  activeFilters?: { teacher: string; course: string };
  onViewDetails: (p: UnifiedProgram) => void;
}) {
  const { preferences } = useSettingsStore();
  const days  = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  const matches = (course: any) => {
    if (activeFilters?.teacher && activeFilters.teacher !== 'all' && (course.instructor || course.teacher) !== activeFilters.teacher) return false;
    if (activeFilters?.course  && activeFilters.course  !== 'all' && (course.course_name || course.course) !== activeFilters.course) return false;
    return true;
  };

  const getDayClasses = (day: string) => {
    const out: any[] = [];
    programs.forEach(p => {
      (p.schedule[day] || []).forEach((c: any) => {
        if (matches(c)) {
          out.push({
            time: c.time, course: c.course_name || c.course,
            room: c.location, instructor: c.instructor || c.teacher,
            program: p.program, semester: p.semester, group: p.group, programData: p,
          });
        }
      });
    });
    return out.sort((a, b) => compareTimeStrings(a.time, b.time));
  };

  // Day view
  if (viewMode === 'day') {
    const dayClasses = getDayClasses(selectedDay);
    if (dayClasses.length === 0) {
      return <EmptyState text={`No classes on ${selectedDay}`} />;
    }
    return (
      <div style={{ position: 'relative' }}>
        {/* Spine line */}
        <div style={{
          position: 'absolute', left: 54, top: 18, bottom: 18,
          width: 1, background: T.border, zIndex: 0,
        }} />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {dayClasses.map((cls, i) => (
            <BrowseTimelineCard key={i} cls={cls} onClick={() => onViewDetails(cls.programData)} />
          ))}
        </div>
      </div>
    );
  }

  // Week view (grid)
  const weekData: Record<string, any[]> = {};
  days.forEach(d => { weekData[d] = getDayClasses(d); });

  return (
    <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
      <div style={{ minWidth: 800 }}>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 8, marginBottom: 10 }}>
          {days.map(day => (
            <div key={day} style={{
              background: day === today ? T.tealBg : T.surface,
              border: `1px solid ${day === today ? T.tealBorder : T.border}`,
              borderRadius: 8, padding: '10px 4px', textAlign: 'center',
            }}>
              <div style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.1em', color: day === today ? T.teal : T.text35 }}>
                {day.substring(0, 3).toUpperCase()}
              </div>
              <div style={{ fontFamily: T.serif, fontSize: 14, color: day === today ? T.teal : T.text50, marginTop: 4, lineHeight: 1 }}>
                {weekData[day].length}
              </div>
            </div>
          ))}
        </div>
        {/* Columns */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 8, alignItems: 'start' }}>
          {days.map(day => (
            <div key={day} style={{ display: 'flex', flexDirection: 'column', gap: 6, minHeight: 200 }}>
              {weekData[day].length === 0 ? (
                <div style={{
                  background: T.surface, border: `1px dashed ${T.border}`,
                  borderRadius: 8, padding: '14px 8px', textAlign: 'center',
                  minHeight: 80, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontFamily: T.mono, fontSize: 8, color: T.text15 }}>—</span>
                </div>
              ) : weekData[day].map((cls, i) => (
                <div
                  key={i}
                  onClick={() => onViewDetails(cls.programData)}
                  style={{
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderTop: `2px solid ${T.teal}`,
                    borderRadius: 8, padding: '9px 10px',
                    cursor: 'pointer', transition: '.15s',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = T.surfaceHov; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = T.surface; }}
                >
                  <div style={{ fontFamily: T.mono, fontSize: 9, color: T.teal, marginBottom: 5, letterSpacing: '.04em' }}>
                    {cls.time?.split('-')[0]?.trim()}
                  </div>
                  <div style={{
                    fontSize: 11, fontWeight: 500, color: T.text, marginBottom: 4,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>{cls.course}</div>
                  {cls.room && (
                    <div style={{ fontSize: 10, color: T.text35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {cls.room}
                    </div>
                  )}
                  <div style={{ fontFamily: T.mono, fontSize: 8, color: T.text15, marginTop: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cls.program}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Timeline card (day view) ────────────────────────────────────────────────
function BrowseTimelineCard({ cls, onClick }: { cls: any; onClick: () => void }) {
  const [hov, setHov] = useState(false);
  const timeParts = cls.time?.split('-') ?? [];
  const startRaw  = timeParts[0]?.trim() ?? '';
  const endRaw    = timeParts[1]?.trim() ?? '';
  const timeH     = startRaw.replace(/(AM|PM)/i, '').trim();
  const timeP     = startRaw.match(/(AM|PM)/i)?.[0] ?? '';

  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      {/* Time column */}
      <div style={{ width: 46, flexShrink: 0, padding: '14px 8px 14px 0', textAlign: 'right' }}>
        <div style={{
          fontFamily: T.mono, fontSize: 11,
          color: hov ? T.teal : T.text50, lineHeight: 1, transition: 'color .15s',
        }}>{timeH}</div>
        <div style={{ fontFamily: T.mono, fontSize: 8, color: T.text25, marginTop: 1 }}>{timeP}</div>
      </div>

      {/* Dot */}
      <div style={{
        width: 14, flexShrink: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', zIndex: 1, padding: '16px 0',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 1,
          background: hov ? T.teal : 'rgba(255,255,255,.18)',
          boxShadow: hov ? `0 0 8px rgba(20,184,166,.55)` : 'none',
          transition: '.15s',
        }} />
      </div>

      {/* Card */}
      <div
        onClick={onClick}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          flex: 1, margin: '8px 0 8px 14px',
          background: hov ? T.surfaceHov : T.surface,
          border: `1px solid ${hov ? T.tealBorder : T.border}`,
          borderRadius: 10, padding: '12px 16px',
          cursor: 'pointer', transition: '.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: T.text, lineHeight: 1.2 }}>
            {cls.course}
          </span>
          <span style={{
            fontFamily: T.mono, fontSize: 9, color: T.text25, letterSpacing: '.06em',
            whiteSpace: 'nowrap', flexShrink: 0,
          }}>{cls.time}</span>
        </div>
        <div style={{ fontFamily: T.mono, fontSize: 8, color: T.text25, letterSpacing: '.07em', marginBottom: 8 }}>
          {cls.program} · SEM {cls.semester} · GRP {cls.group}
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {cls.instructor && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.text35 }}>
              <User size={10} color={T.teal} /> {cls.instructor}
            </div>
          )}
          {cls.room && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.text35 }}>
              <MapPin size={10} color={T.teal} /> {cls.room}
            </div>
          )}
          {endRaw && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.text25, marginLeft: 'auto' }}>
              <Clock size={10} color={T.text25} /> until {endRaw}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
function EmptyState({ text }: { text: string }) {
  return (
    <div style={{
      background: T.surface, border: `1px dashed ${T.border}`,
      borderRadius: 14, padding: '48px 24px', textAlign: 'center',
    }}>
      <Calendar size={28} color={T.text15} style={{ margin: '0 auto 12px', display: 'block' }} />
      <p style={{ fontFamily: T.mono, fontSize: 10, letterSpacing: '.12em', color: T.text25, margin: 0 }}>
        {text.toUpperCase()}
      </p>
    </div>
  );
}

// ─── Details modal ────────────────────────────────────────────────────────────
function TimetableDetailsModal({
  program, isOpen, onClose, activeFilters,
}: {
  program: UnifiedProgram; isOpen: boolean; onClose: () => void;
  activeFilters?: { teacher: string; course: string };
}) {
  const { preferences } = useSettingsStore();
  const allDays = getOrderedDays(preferences.weekStartsOn, preferences.showWeekends);

  const getFiltered = (ds: any[]) => {
    if (!ds) return [];
    let out = ds;
    if (activeFilters?.teacher && activeFilters.teacher !== 'all') out = out.filter((c: any) => (c.instructor || c.teacher) === activeFilters.teacher);
    if (activeFilters?.course  && activeFilters.course  !== 'all') out = out.filter((c: any) => (c.course_name || c.course) === activeFilters.course);
    return out;
  };

  const availableDays = allDays.filter(day => {
    const ds = program.schedule[day];
    if (!ds || ds.length === 0) return false;
    return ds.some((c: any) => {
      let m = true;
      if (activeFilters?.teacher && activeFilters.teacher !== 'all') m = m && (c.instructor || c.teacher) === activeFilters.teacher;
      if (activeFilters?.course  && activeFilters.course  !== 'all') m = m && (c.course_name || c.course) === activeFilters.course;
      return m;
    });
  });

  const [selectedDay, setSelectedDay] = useState(availableDays[0] ?? 'Monday');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="border-0 p-0"
        style={{
          background: '#111', width: '95vw', maxWidth: 680,
          maxHeight: '88vh', overflowY: 'auto',
          borderRadius: 16, border: `1px solid ${T.borderHov}`,
          boxShadow: '0 24px 64px rgba(0,0,0,.7)',
        }}
      >
        {/* Modal header */}
        <div style={{
          padding: '22px 24px 18px',
          borderBottom: `1px solid ${T.border}`,
          background: 'linear-gradient(135deg, rgba(20,184,166,.07) 0%, transparent 60%)',
          position: 'relative',
        }}>
          <div style={{ position: 'absolute', top: -30, right: -20, width: 140, height: 140, background: 'radial-gradient(circle, rgba(20,184,166,.12) 0%, transparent 65%)', pointerEvents: 'none' }} />
          <MonoLabel color={T.teal}>TIMETABLE DETAILS</MonoLabel>
          <h2 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, letterSpacing: '-.01em', margin: '8px 0 6px', lineHeight: 1.2 }}>
            {program.program}
          </h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {[program.department, `Sem ${program.semester}`, `Group ${program.group}`].map(t => (
              <span key={t} style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.08em', color: T.text35 }}>{t}</span>
            ))}
          </div>
          <button
            onClick={onClose}
            style={{
              position: 'absolute', top: 18, right: 18,
              width: 30, height: 30, borderRadius: 8,
              background: T.surface, border: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: T.text50, cursor: 'pointer',
            }}
          ><X size={14} /></button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          {availableDays.length > 0 && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
              {availableDays.map(day => (
                <button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  style={{
                    padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                    background: selectedDay === day ? T.tealBg : T.surface,
                    border: `1px solid ${selectedDay === day ? T.tealBorder : T.border}`,
                    color: selectedDay === day ? T.teal : T.text50,
                    fontFamily: T.mono, fontSize: 9, letterSpacing: '.08em',
                    whiteSpace: 'nowrap', transition: '.15s',
                  }}
                >{day.substring(0, 3).toUpperCase()}</button>
              ))}
            </div>
          )}

          {availableDays.length > 0 && (() => {
            const courses = getFiltered(program.schedule[selectedDay]);
            return courses.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {courses.map((course: any, i: number) => (
                  <div key={i} style={{
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderLeft: `3px solid ${T.teal}`,
                    borderRadius: 10, padding: '14px 16px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                      <h3 style={{ fontSize: 13, fontWeight: 500, color: T.text, margin: 0, lineHeight: 1.3, flex: 1 }}>
                        {course.course_name || course.course}
                      </h3>
                      <span style={{
                        fontFamily: T.mono, fontSize: 9, letterSpacing: '.06em',
                        color: T.teal, background: T.tealBg, border: `1px solid ${T.tealBorder}`,
                        borderRadius: 5, padding: '3px 8px', whiteSpace: 'nowrap', flexShrink: 0,
                      }}>{course.time}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                      {[
                        { Icon: User,   text: course.instructor || course.teacher || 'TBA' },
                        { Icon: MapPin, text: course.location || 'TBA' },
                      ].map(({ Icon, text }) => (
                        <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: T.text35 }}>
                          <Icon size={11} color={T.teal} /> {text}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState text={`No matching classes on ${selectedDay}`} />
            );
          })()}

          {/* Weekly summary */}
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: '14px 16px' }}>
            <MonoLabel>WEEKLY SUMMARY</MonoLabel>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))', gap: 8, marginTop: 12 }}>
              {availableDays.map(day => {
                const count = getFiltered(program.schedule[day]).length;
                return (
                  <div key={day} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    padding: '8px 6px',
                    background: count > 0 ? T.tealBg : 'rgba(255,255,255,.02)',
                    border: `1px solid ${count > 0 ? T.tealBorder : T.border}`,
                    borderRadius: 8,
                  }}>
                    <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.08em', color: count > 0 ? T.teal : T.text25 }}>
                      {day.substring(0, 3).toUpperCase()}
                    </span>
                    <span style={{ fontFamily: T.serif, fontSize: 18, color: count > 0 ? T.teal : T.text35, lineHeight: 1 }}>
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
